<template>
  <div class="radioTitle">
    <span v-html="content"></span>
    <span v-if="isRequired" class="required" v-html="requiredMessage"></span>
  </div>

  <el-radio-group
    v-model="selectedOption"
    @change="onChange"
    class="radioGroup relative"
  >
    <el-radio
      v-for="option in options"
      :key="option.value"
      :label="option.value"
      border
      :disabled="this.disabled"
      :class="{
        expandedOption: option.value == 'ASIAN' && showExpandedInput,
      }"
    >
      {{ option.label }}
    </el-radio>
    <div class="expandedContainer" v-if="showExpandedInput">
      <div class="title">Please specify</div>
      <el-input
        v-model="this.gutZoomerQnr.basic_info.specific_asian_ethnicity"
        class="expandInputField"
        :type="this.dynamic_input_type"
        :disabled="this.qnrIsCompleted"
        @input="
          handleValueChange('basic_info.specific_asian_ethnicity', $event)
        "
      />
    </div>
  </el-radio-group>
</template>

<script>
import { useGutZoomerStore } from "@/stores/GutZoomerStore.ts";
import { mapState, mapActions } from "pinia";
import { useWindowSize } from "@vueuse/core";
export default {
  name: "QuestionnaireRadio",
  props: {
    disabled: {
      type: Boolean,
      default: false,
    },
    isRequired: {
      type: Boolean,
    },
    content: {
      type: String,
      required: true,
    },
    height: {
      type: String,
    },
    width: {
      type: String,
    },
    mobileWidth: {
      type: String,
    },
    options: {
      type: Array,
      required: true,
    },
    value: {
      default: "",
    },
    requiredMessage: {
      type: String,
    },
  },
  setup() {
    const { width: windowWidth } = useWindowSize();
    return {
      windowWidth,
    };
  },
  watch: {
    value: {
      immediate: true,
      handler(newValue) {
        this.selectedOption = newValue;
      },
    },
    selectedOption(newValue) {
      if (newValue === "ASIAN") {
        this.showExpandedInput = true;
      } else {
        this.showExpandedInput = false;
      }
    },
  },
  emits: ["onChange"],
  data() {
    return {
      selectedOption: this.value,
      radioHeight: this.height === "tall" ? "80px" : "42px",
      radioWidth: this.width === "wide" ? "27vw" : this.width,
      showExpandedInput: false,
    };
  },
  computed: {
    ...mapState(() => useGutZoomerStore(), ["gutZoomerQnr", "qnrIsCompleted"]),
    isMobile() {
      return this.windowWidth <= 808;
    },
    dynamic_input_type() {
      return this.isMobile ? "textarea" : "text";
    },
  },
  methods: {
    ...mapActions(() => useGutZoomerStore(), ["setGutZoomerQnrSingleField"]),
    handleValueChange(key, value) {
      this.setGutZoomerQnrSingleField(key, value);
    },
    onChange() {
      this.$emit("onChange", this.selectedOption);
    },
  },
};
</script>

<style scoped>
.expandedOption {
  margin-bottom: 100px;
}

.expandedContainer {
  padding: 12px 24px;
  border-radius: 4px;
  background-color: #f3f6f8;
  position: absolute;
  top: 160px;
}

.title {
  font-family: Roboto;
  font-weight: 400;
  font-size: 16px;
  line-height: 100%;
  letter-spacing: 0.17px;
  margin-bottom: 10px;
}

.expandInputField {
  height: 42px;
  font-weight: 400;
  line-height: 19px;
  letter-spacing: 0.4px;
  color: #000000;
  border-radius: 4px;
  width: 760px;
}

.radioTitle {
  font-size: 16px;
  font-weight: 600;
  line-height: 19px;
  letter-spacing: 0.175px;
  margin-bottom: 12px;
  white-space: pre-line;
}

.required {
  color: #ff1d00;
  line-height: 20px;
}

.el-radio-group {
  display: flex;
  /* gap:15px; */
  padding: 0px;
  box-sizing: border-box;
  flex-wrap: wrap;
  row-gap: 12px;
  column-gap: 18px;
  align-items: flex-start;
}

:deep(.el-radio.is-bordered) {
  width: v-bind("radioWidth");
  /* min-width: 284px; */
  min-height: v-bind("radioHeight");
  height: fit-content;
  border-radius: 4px;
  border: 1px solid #bcccdc;
  margin-right: 0px;
  background-color: #ffffff;
}

:deep(.el-radio.is-bordered.is-disabled) {
  background-color: #f2f2f2;
  border: 1px solid #bcccdc;
}

:deep(.el-radio__label) {
  font-size: 16px;
  font-weight: 400;
  line-height: 19px;
  letter-spacing: 0.46px;
  color: #000000;
  white-space: normal;
}

:deep(.el-radio.is-bordered.is-checked) {
  border: 1px solid #20a8c3;
  background-color: rgba(32, 168, 195, 0.1);
}

:deep(.el-radio__input.is-checked + .el-radio__label) {
  color: #20a8c3 !important;
}

:deep(.el-radio__input.is-disabled + span.el-radio__label) {
  color: #000000;
}

:deep(.el-radio__inner) {
  height: 20px !important;
  width: 20px !important;
  border: 2px #000000 solid;
  margin-left: 5px;
  margin-right: 5px;
}

:deep(.el-radio__input.is-disabled .el-radio__inner) {
  border: 2px #000000 solid;
  background: transparent !important;
}

:deep(.el-radio__input.is-checked .el-radio__inner) {
  border: 2px #20a8c3 solid !important;
  background: transparent !important;
}

:deep(.el-radio__input.is-checked .el-radio__inner::after) {
  content: "";
  display: block;
  width: 10px;
  height: 10px;
  background-color: #20a8c3;
  border-radius: 50%;
  box-sizing: border-box;
  border: 2px #20a8c3 solid !important;
}

:deep(.el-textarea__inner) {
  caret-color: #000 !important;
  white-space: normal;
  font-weight: 400;
  line-height: 19px;
  letter-spacing: 0.4px;
  color: #000000;
  resize: none;
  height: 126px;
  box-shadow: none;
  padding: 12px 16px;
  border: 1px solid #bcccdc;
}

@media only screen and (max-width: 808px) {
  .el-radio-group {
    gap: 8px;
  }
  :deep(.el-radio.is-bordered) {
    min-height: 42px;
    height: fit-content;
    width: v-bind("mobileWidth");
  }

  .expandedOption {
    margin-bottom: 170px;
  }

  .title {
    font-size: 14px;
    line-height: 16px;
    margin-bottom: 8px;
  }

  .radioTitle {
    font-size: 14px;
  }

  :deep(.el-radio__label) {
    font-size: 14px;
  }

  .el-radio div {
    font-size: 14px !important;
    font-weight: 400 !important;
  }

  :deep(.el-radio__inner) {
    height: 16px !important;
    width: 16px !important;
    margin-left: 8px;
    margin-right: 8px;
    border: 1px #000000 solid !important;
  }

  :deep(.el-radio__input.is-checked .el-radio__inner::after) {
    width: 8px;
    height: 8px;
    border: 1px #20a8c3 solid !important;
  }

  .expandedContainer {
    padding: 12px 16px;
    font-size: 14px;
    position: absolute;
    top: 255px;
  }

  .expandInputField {
    height: 100px;
    font-size: 14px;
    font-weight: 400;
    line-height: 19px;
    letter-spacing: 0.4px;
    color: #000000;
    border-radius: 4px;
    width: 310px;
  }

  .expandedContainer :deep(.el-textarea__inner) {
    height: 100px;
    font-size: 14px;
    line-height: 16px;
  }
}
</style>
